import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { supabase } from '@/lib/supabase';
import { authStateAtom } from '@/lib/auth'; // Keep updating the legacy atom for now?
import {
  currentCommunityAtom,
  communityLoadingAtom,
  communityErrorAtom,
} from '@/lib/stores/community'; // Need current community for role check, import other atoms for reset
import type { User, Session } from '@supabase/supabase-js';
import { authLogger } from '@/lib/logger';
import type {
  Profile,
  CommunityMember,
  AuthState,
  Community,
} from '@/types/auth';

// Combined type for user state within the hook - Use Profile directly
type AuthUser = User & { profile?: Profile | null }; // Supabase User + our Profile

// Cache to avoid duplicate requests during the same render cycle
const profileCache = new Map<string, Profile>();
const membershipCache = new Map<string, CommunityMember[]>();
const communityCache = new Map<string, Community>(); // Added community cache

/**
 * AI Context: useAuth Hook
 *
 * Purpose: Provides reactive authentication state based on Supabase auth changes.
 *          Includes user session, profile data (with global role),
 *          all community memberships, loading state, and role checking.
 * Location: src/lib/hooks/useAuth.ts
 * Usage: Used by ProtectedRoute and any component needing auth state or role checks.
 */
export const useAuth = () => {
  const [session, setSession] = useState<Session | null>(null);
  // Store Supabase User + Profile together
  const [user, setUser] = useState<AuthUser | null>(null);
  const [memberships, setMemberships] = useState<CommunityMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Track if auth listener is already set up
  const authListenerSetup = useRef(false);

  // Performance tracking for data fetching
  const fetchStartTime = useRef<number>(0);

  // Jotai state access
  const setAuthState = useSetAtom(authStateAtom);
  const currentCommunityValue = useAtomValue(currentCommunityAtom);
  const setCurrentCommunity = useSetAtom(currentCommunityAtom);
  const setCommunityLoading = useSetAtom(communityLoadingAtom);
  const setCommunityError = useSetAtom(communityErrorAtom);

  const clearJotaiAtoms = useCallback(() => {
    setAuthState({
      user: null,
      community: null, // Assuming this is part of your AuthState type, reset it
      isLoading: false, // After logout/no session, not actively loading auth state itself
      error: null,
    });
    setCurrentCommunity(null);
    setCommunityLoading(false); // Reset community loading state
    setCommunityError(null); // Reset community error state

    // Clear caches as well
    profileCache.clear();
    membershipCache.clear();
    communityCache.clear(); // Clear community cache
  }, [
    setAuthState,
    setCurrentCommunity,
    setCommunityLoading,
    setCommunityError,
  ]);

  // Fetch profile and memberships when user session changes
  useEffect(() => {
    if (!session?.user) {
      setUser(null);
      setMemberships([]);
      return;
    }

    let isMounted = true;
    const fetchUserData = async () => {
      // Start performance measurement
      fetchStartTime.current = performance.now();
      setLoading(true);

      try {
        const userId = session.user.id;

        // Check cache first
        const cachedProfile = profileCache.get(userId);
        const cachedMemberships = membershipCache.get(userId);

        // Fetch profile if not cached
        let loadedProfile: Profile | null = null;
        if (cachedProfile) {
          loadedProfile = cachedProfile;
        } else {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

          if (profileError) throw profileError;
          loadedProfile = (profileData as Profile) ?? null;

          // Cache the result
          if (loadedProfile) {
            profileCache.set(userId, loadedProfile);
          }
        }

        // Fetch memberships if not cached
        let processedMemberships: CommunityMember[] = [];
        if (cachedMemberships) {
          processedMemberships = cachedMemberships;
        } else {
          const { data: membershipData, error: membershipError } =
            await supabase
              .from('community_members')
              .select('*, communities ( slug )')
              .eq('profile_id', userId);

          if (membershipError) throw membershipError;

          processedMemberships =
            membershipData?.map((m) => ({
              ...m,
              slug: m.communities?.slug,
            })) ?? [];

          // Cache the result
          membershipCache.set(userId, processedMemberships);
        }

        // Fetch community details based on JWT app_metadata
        let loadedCommunity: Community | null = null;
        const communityIdFromJwt = session.user.app_metadata?.community_id as
          | string
          | undefined;

        if (communityIdFromJwt) {
          const cachedCommunity = communityCache.get(communityIdFromJwt);
          if (cachedCommunity) {
            loadedCommunity = cachedCommunity;
          } else {
            const { data: communityData, error: communityError } =
              await supabase
                .from('communities')
                .select('*')
                .eq('id', communityIdFromJwt)
                .single();

            if (communityError) {
              // Potentially set an error state or proceed with null community
            } else {
              loadedCommunity = communityData as Community;
              if (loadedCommunity) {
                communityCache.set(communityIdFromJwt, loadedCommunity);
              }
            }
          }
        } else {
          // No community_id found in JWT app_metadata
        }

        if (isMounted) {
          setUser({ ...session.user, profile: loadedProfile });
          setMemberships(processedMemberships);

          // Calculate and log performance
          const fetchDuration = performance.now() - fetchStartTime.current;

          setAuthState((prev) => ({
            ...prev,
            user: loadedProfile,
            community: loadedCommunity, // Set the fetched community object
            isLoading: false,
            error: null,
          }));
          setError(null);
        }
      } catch (err: any) {
        const msg = err.message || 'Failed to load user data';
        if (isMounted) {
          setError(msg);
          setUser({ ...session.user, profile: null });
          setMemberships([]);
          setAuthState((prev) => ({
            ...prev,
            user: null,
            community: null, // Ensure community is nulled out here too if profile fetch fails
            isLoading: false,
            error: msg,
          }));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchUserData();
    return () => {
      isMounted = false;
    };
  }, [session, setAuthState]); // Re-run when session changes

  // Listener for auth state changes
  useEffect(() => {
    // Ensure we only set up the auth listener once
    if (authListenerSetup.current) {
      return () => {};
    }

    setLoading(true);

    authListenerSetup.current = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Check if we should skip redirects during this auth state change (for JWT updates during onboarding)
      const skipRedirect =
        localStorage.getItem('skip_auth_redirect') === 'true';
      const onboardingComplete =
        localStorage.getItem('onboarding_complete') === 'true';
      const communitySlug = localStorage.getItem('onboarding_community_slug');

      if (skipRedirect) {
        // Removed console.log
      }

      if (onboardingComplete && communitySlug) {
        // Clear these flags to prevent future issues
        localStorage.removeItem('onboarding_complete');
        localStorage.removeItem('onboarding_community_slug');

        // If we're on the onboarding page and receive a TOKEN_REFRESHED event
        // with onboardingComplete flag, we know this is part of the finalization
        // The redirect will happen separately via getSubdomainUrl
        if (
          event === 'TOKEN_REFRESHED' &&
          window.location.pathname.includes('/onboarding')
        ) {
          // Removed console.log
        }
      }

      // Standard handling for different auth events, relying on @supabase/ssr client for session management
      if (
        event === 'SIGNED_IN' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'INITIAL_SESSION'
      ) {
        setUser(session?.user ?? null);
        setSession(session); // This will trigger the profile/membership fetch useEffect
        if (!session) {
          // Explicitly handle case where INITIAL_SESSION might have no session
          setMemberships([]);
          clearJotaiAtoms(); // Call clearJotaiAtoms which handles all necessary resets
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setSession(null);
        setMemberships([]);
        clearJotaiAtoms();
        setLoading(false);
      } else if (event === 'PASSWORD_RECOVERY') {
        setLoading(false);
      } else if (event === 'USER_UPDATED') {
        setUser(session?.user ?? null);
        // Profile fetching for USER_UPDATED is handled by the other useEffect based on session change
      } else if (event === 'USER_DELETED') {
        setUser(null);
        setSession(null);
        setMemberships([]);
        clearJotaiAtoms();
        setLoading(false);
      }
      // The fetchUserData effect will run if the session object instance changes and contains a user.
    });

    // Get initial session in a non-blocking way
    supabase.auth
      .getSession()
      .then(
        ({
          data: { session: initialSessionData },
          error: initialSessionError,
        }) => {
          if (!initialSessionData) {
            setLoading(false); // Only set loading false if session is truly not found initially
          }
          // If a session IS found by getSession(), onAuthStateChange should also fire with it.
          // The setLoading(true) at the start of onAuthStateChange and setLoading(false) in fetchUserData
          // or when session is null in onAuthStateChange should handle loading states.
        }
      );

    return () => {
      authListenerSetup.current = false;
      subscription.unsubscribe();
    };
  }, [setAuthState]);

  // Memoize permission values for common roles to avoid recalculation
  const isAdmin = useMemo(() => {
    const profile = user?.profile;
    if (!profile) return false;
    return profile.role === 'admin' || session?.user?.app_metadata?.role === 'admin';
  }, [user?.profile?.role, session?.user?.app_metadata?.role]);

  const isOwner = useMemo(() => {
    const profile = user?.profile;
    if (!profile) return false;
    return profile.role === 'owner' || session?.user?.app_metadata?.role === 'owner';
  }, [user?.profile?.role, session?.user?.app_metadata?.role]);

  // --- Role Checking Function with fast paths for owners/admins ---
  const hasRole = useCallback(
    (requiredRole: string): boolean => {
      const profile = user?.profile;
      if (!profile) {
        return false;
      }

      // Fast path for admin - use memoized value
      if (requiredRole === 'admin') {
        return isAdmin;
      }

      // Fast path for owner - use memoized value
      if (requiredRole === 'owner') {
        return isOwner;
      }

      // Community owners have all permissions in their community - fast return
      if (isOwner && currentCommunityValue?.owner_id === user?.id) {
        return true; // Owners have all roles in their own community
      }

      if (requiredRole === 'member') {
        // For member role, check if user has member role in profile OR has any community membership
        const isMember = profile.role === 'member';
        const hasAnyMembership = memberships.length > 0;
        
        // If we have a specific community context, check membership in that community
        const communityId = currentCommunityValue?.id;
        if (communityId) {
          const membership = memberships.find(
            (m) => m.community_id === communityId
          );
          return membership?.role === 'member' || (isMember && membership);
        }
        
        // Fallback: if no specific community context, accept if user is generally a member
        return isMember || hasAnyMembership;
      }

      // For other roles, require specific community context
      const communityId = currentCommunityValue?.id;
      if (!communityId) {
        return false;
      }

      const membership = memberships.find(
        (m) => m.community_id === communityId
      );
      const hasCommunityRole = membership?.role === requiredRole;
      return hasCommunityRole;
    },
    [user, memberships, currentCommunityValue, isAdmin, isOwner]
  );

  return { user, session, loading, error, hasRole, memberships };
};
